import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
  FaCog,
  FaFire,
  FaHeart,
  FaPuzzlePiece,
  FaRegHeart,
  FaSignal,
  FaStepBackward,
  FaStepForward,
} from "react-icons/fa";
import Board from "@/chessBoard/components/Board";
import { Chess } from "@chesshotel/chesslib";
import { convertMoves } from "@/util/moves";
import { ChessSets } from "@chesshotel/constants";
import { cn } from "@/lib/utils";
import { move2uci, uci2move } from "@chesshotel/gameReview";
import { Chess as SanChess, MoveShort, UciMove } from "@chesshotel/chess";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/ToolTip";
import { useSettings } from "@/redux/settings";
import MoveIcon from "@/components/common/move-icon";
import { MoveType } from "@chesshotel/types";
import { useUser } from "@/redux/user";
import { api } from "@/services/api";
import ResizableBoardFrame from "@/components/board/ResizableBoardFrame";
import {
  getPuzzleLeagueMedalImageSrc,
  getPuzzleLeagueRankMedalImageSrc,
  getPuzzleLeagueTier,
  getPuzzlePlacementProgressView,
  getPuzzleRankProgressView,
  PuzzleSolvedProgress,
} from "./puzzleMedals";
import Popup from "@/components/ui/popup";
import {
  PUZZLE_DIFFICULTY_OPTIONS,
  PuzzleDifficultyKey,
} from "./puzzleDifficulty";

enum PuzzleState {
  START,
  CORRECT_MOVE,
  INCORRECT_MOVE,
  DONE,
  FAILED,
}

const STARTING_PUZZLE_RATING = 800;

function getPuzzleStreakBonus(cleanSolveStreak: number) {
  if (cleanSolveStreak <= 1) return 0;

  return Math.min(40, cleanSolveStreak * 4);
}

export type PuzzleRatingUpdate = {
  newElo: number;
  eloChange: number;
  won?: boolean;
  gameNr?: number;
  leagueName?: string;
  divisionId?: number;
  solvedCount?: number;
  points?: number;
  pointsEarned?: number;
  streakBonus?: number;
  cleanSolveStreak?: number;
  rank?: number | null;
  nextRank?: number | null;
  nextRankPoints?: number | null;
};

type PuzzleBoardProps = {
  fen: string;
  moves: MoveShort[] | string;
  onComplete?: () => void;
  onNext?: () => void;
  onResult?: (won: boolean, cleanSolve: boolean) => void;
  onRatingChange?: (ratingUpdate: PuzzleRatingUpdate) => void;
  puzzleRating?: number;
  userElo?: number;
  gameNr?: number;
  leagueName?: string;
  solvedProgress: PuzzleSolvedProgress;
  puzzlePoints: number;
  lastPointsEarned?: number | null;
  lastStreakBonus?: number;
  rank?: number | null;
  nextRank?: number | null;
  nextRankPoints?: number | null;
  cleanSolveStreak?: number;
  nextLoading?: boolean;
  puzzleDifficulty: PuzzleDifficultyKey;
  ratingKFactor?: number;
  ratingRevealed?: boolean;
  onPuzzleDifficultyChange?: (difficulty: PuzzleDifficultyKey) => void;
};

function PuzzleStatusCard({
  statusTitle,
  statusText,
  puzzleRating,
  lives,
}: {
  statusTitle: ReactNode;
  statusText: ReactNode;
  puzzleRating?: number;
  lives: number;
}) {
  return (
    <div className="relative min-h-[96px] rounded-md bg-white py-4 pl-4 pr-24 shadow-sm">
      <div className="min-w-0">
        <div className="mb-1">{statusTitle}</div>
        <div className="text-sm">{statusText}</div>
      </div>
      <div className="absolute right-4 top-4">
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              tabIndex={0}
              className="inline-flex cursor-help items-center gap-1 rounded-full bg-athens-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700 outline-none focus-visible:ring-2 focus-visible:ring-altblue-300"
            >
              <FaSignal className="text-orange-500" />
              <span>Puzzle {puzzleRating || "?"}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6}>
            <p>Puzzle difficulty</p>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="absolute bottom-4 right-4">
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              tabIndex={0}
              className="cursor-help rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-altblue-300"
            >
              <PuzzleLives
                lives={lives}
                className="text-red-500"
                emptyClassName="text-gray-300"
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6}>
            <p>Mistakes left</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

function PuzzleLives({
  lives,
  className,
  emptyClassName,
}: {
  lives: number;
  className?: string;
  emptyClassName?: string;
}) {
  return (
    <div className={cn("flex items-center gap-1 text-xs", className)}>
      {[...Array(3)].map((_, i) => (
        <span key={i}>
          {i < lives ? (
            <FaHeart />
          ) : (
            <FaRegHeart
              className={emptyClassName || "text-current opacity-45"}
            />
          )}
        </span>
      ))}
    </div>
  );
}

function PuzzleUserStanding({
  rating,
  leagueName,
  ratingRevealed,
}: {
  rating: number;
  leagueName?: string;
  ratingRevealed: boolean;
}) {
  if (!ratingRevealed) {
    return (
      <div className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold text-athens-gray-50">
        Placement
      </div>
    );
  }

  const tier = getPuzzleLeagueTier({ rating, leagueName });

  return (
    <div className="flex items-center gap-2 rounded-full bg-white/15 py-1 pl-1.5 pr-3 text-xs font-bold text-athens-gray-50">
      <img
        src={getPuzzleLeagueMedalImageSrc(tier)}
        alt={`${tier.label} puzzle league`}
        className="h-6 w-6 shrink-0 object-contain"
      />
      <span>{tier.label}</span>
      <span className="h-4 w-px bg-white/30" />
      <span>{rating}</span>
    </div>
  );
}

function PuzzleProgressBar({
  rating,
  leagueName,
  ratingRevealed,
  solvedProgress,
  puzzlePoints,
  lastPointsEarned,
  lastStreakBonus = 0,
  cleanSolveStreak = 0,
  nextRank,
  nextRankPoints,
}: {
  rating: number;
  leagueName?: string;
  ratingRevealed: boolean;
  solvedProgress: PuzzleSolvedProgress;
  puzzlePoints: number;
  lastPointsEarned?: number | null;
  lastStreakBonus?: number;
  cleanSolveStreak?: number;
  nextRank?: number | null;
  nextRankPoints?: number | null;
}) {
  const placementProgress = getPuzzlePlacementProgressView(
    solvedProgress.solved,
  );
  const rankProgress = getPuzzleRankProgressView({
    points: puzzlePoints,
    nextRank,
    nextRankPoints,
  });
  const currentTier = getPuzzleLeagueTier({ rating, leagueName });
  const placementTier = getPuzzleLeagueTier({ rating: STARTING_PUZZLE_RATING });

  const title = ratingRevealed
    ? `Next: ${currentTier.label} ${rankProgress.title}`
    : "Complete placement";
  const progressText = ratingRevealed
    ? rankProgress.progressText
    : `${placementProgress.solved} / ${placementProgress.target} solved`;
  const percent = ratingRevealed
    ? rankProgress.percent
    : placementProgress.percent;
  const rewardTier = ratingRevealed
    ? {
        ...currentTier,
        rank: rankProgress.nextRank || currentTier.rank,
      }
    : placementTier;

  return (
    <div className="mt-3 px-1">
      <div className="mb-2 flex items-end justify-between gap-3">
        <div className="min-w-0 text-sm font-bold text-gray-800 truncate">
          {title}
        </div>
        <div className="shrink-0 text-xs font-bold text-gray-500">
          {progressText}
        </div>
      </div>
      {ratingRevealed &&
        lastPointsEarned !== null &&
        lastPointsEarned !== undefined &&
        lastPointsEarned > 0 && (
          <div className="flex items-center gap-2 mb-2 text-xs font-bold text-gray-600">
            <span className="text-green-600">+{lastPointsEarned} points</span>
            {lastStreakBonus > 0 && (
              <span className="text-green-600">+{lastStreakBonus} bonus</span>
            )}
            {cleanSolveStreak > 0 && (
              <span className="inline-flex items-center gap-1 text-orange-500">
                <FaFire />
                <span>{cleanSolveStreak}</span>
              </span>
            )}
          </div>
        )}
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="h-3 overflow-hidden rounded-full bg-athens-gray-200">
            <div
              className="h-full rounded-full bg-altblue-500 transition-[width] duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
        <img
          src={
            ratingRevealed
              ? getPuzzleLeagueRankMedalImageSrc(rewardTier, rewardTier.rank)
              : getPuzzleLeagueMedalImageSrc(rewardTier)
          }
          alt={`${rewardTier.label} puzzle reward`}
          className={cn(
            "h-10 w-10 shrink-0 object-contain drop-shadow-sm",
            !ratingRevealed && "opacity-45 grayscale",
          )}
        />
      </div>
    </div>
  );
}

function PuzzleDifficultyPopup({
  selectedDifficulty,
  onSelect,
  onClose,
}: {
  selectedDifficulty: PuzzleDifficultyKey;
  onSelect: (difficulty: PuzzleDifficultyKey) => void;
  onClose: () => void;
}) {
  return (
    <Popup title="Puzzle difficulty" onClose={onClose} maxWidth="420px">
      <div className="grid gap-2">
        {PUZZLE_DIFFICULTY_OPTIONS.map((option) => {
          const selected = option.key === selectedDifficulty;

          return (
            <button
              key={option.key}
              type="button"
              className={cn(
                "flex w-full items-center justify-between rounded-md border px-3 py-3 text-left transition-colors",
                selected
                  ? "border-altblue-500 bg-altblue-50 text-altblue-800"
                  : "border-athens-gray-200 bg-white text-gray-700 hover:bg-athens-gray-100",
              )}
              onClick={() => {
                onSelect(option.key);
                onClose();
              }}
            >
              <span className="min-w-0">
                <span className="block text-sm font-bold">{option.label}</span>
                <span className="block text-xs text-muted-foreground">
                  {option.description}
                </span>
              </span>
              {selected && (
                <span className="ml-3 rounded-full bg-altblue-500 px-2 py-0.5 text-xs font-bold text-white">
                  On
                </span>
              )}
            </button>
          );
        })}
      </div>
    </Popup>
  );
}

function PuzzleSolutionLine({ solutionLine }: { solutionLine: string }) {
  if (!solutionLine) return null;

  return (
    <div className="mt-3 overflow-hidden text-sm text-gray-700 border rounded-md border-athens-gray-200 bg-athens-gray-100">
      <div className="border-b border-athens-gray-200 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
        Solution
      </div>
      <div className="px-3 py-2 font-mono text-xs leading-5">
        {solutionLine}
      </div>
    </div>
  );
}

function PuzzleMoveList({
  startFen,
  moves,
  moveIndex,
  selectedMoveIndex,
}: {
  startFen: string;
  moves: MoveShort[];
  moveIndex: number;
  selectedMoveIndex: number;
}) {
  const startFenParts = startFen.split(" ");
  const startTurn = startFenParts[1] === "b" ? "b" : "w";
  const startMoveNumber = Math.max(
    1,
    Number.parseInt(startFenParts[5] || "1", 10) || 1,
  );

  const moveRecords = useMemo(() => {
    let chess: SanChess | null = null;
    try {
      chess = new SanChess(startFen);
    } catch {
      chess = null;
    }

    return moves.map((move, index) => {
      const color = index % 2 === 0 ? startTurn : startTurn === "w" ? "b" : "w";
      const rowNumber =
        startMoveNumber + Math.floor((index + (startTurn === "b" ? 1 : 0)) / 2);

      if (!chess) {
        return {
          ...move,
          color,
          rowNumber,
          san: move2uci(move),
        };
      }

      try {
        const playedMove = chess.move(move);
        return {
          ...move,
          color: playedMove?.color || color,
          rowNumber,
          san: playedMove?.san || move2uci(move),
        };
      } catch {
        return {
          ...move,
          color,
          rowNumber,
          san: move2uci(move),
        };
      }
    });
  }, [moves, startFen, startMoveNumber, startTurn]);

  if (moves.length === 0 || moveIndex <= 0) return null;

  const visibleMoves = moveRecords.slice(0, moveIndex);
  const groupedRows = visibleMoves.reduce<
    {
      rowNumber: number;
      whiteMove: (typeof moveRecords)[number] | null;
      blackMove: (typeof moveRecords)[number] | null;
      whiteIndex: number | null;
      blackIndex: number | null;
    }[]
  >((rows, move, index) => {
    let row = rows.find((item) => item.rowNumber === move.rowNumber);

    if (!row) {
      row = {
        rowNumber: move.rowNumber,
        whiteMove: null,
        blackMove: null,
        whiteIndex: null,
        blackIndex: null,
      };
      rows.push(row);
    }

    if (move.color === "w") {
      row.whiteMove = move;
      row.whiteIndex = index;
    } else {
      row.blackMove = move;
      row.blackIndex = index;
    }

    return rows;
  }, []);

  const rows = groupedRows.map((row) => {
    const rowClass =
      row.rowNumber % 2 === 0 ? "bg-athens-gray-100" : "bg-athens-gray-150";
    const isWhiteSelected = row.whiteIndex === selectedMoveIndex - 1;
    const isBlackSelected = row.blackIndex === selectedMoveIndex - 1;

    return (
      <div
        key={`${row.rowNumber}-${row.whiteMove?.from || ""}${
          row.blackMove?.from || ""
        }`}
        className={cn(
          "box-border flex h-[36px] min-h-[36px] w-full shrink-0 items-center text-sm leading-[36px]",
          rowClass,
        )}
      >
        <div className="w-10 shrink-0 pl-3.5 font-semibold text-gray-500/80">
          {row.rowNumber}
        </div>
        <div
          className={cn(
            "flex h-full min-w-0 flex-1 items-center px-2 text-left font-semibold",
            isWhiteSelected ? "text-primary" : "text-gray-600",
          )}
        >
          <span className="block truncate">
            {row.whiteMove ? row.whiteMove.san : ""}
          </span>
        </div>
        <div
          className={cn(
            "flex h-full min-w-0 flex-1 items-center px-2 text-left font-semibold",
            isBlackSelected ? "text-primary" : "text-gray-600",
          )}
        >
          <span className="block truncate">
            {row.blackMove ? row.blackMove.san : ""}
          </span>
        </div>
      </div>
    );
  });

  return (
    <div className="mt-3 flex max-h-[144px] min-h-[36px] flex-col overflow-y-auto rounded-md bg-athens-gray-150 text-gray-600">
      {rows}
    </div>
  );
}

function buildPuzzlePositions(startFen: string, moves: MoveShort[]) {
  const positions: { fen: string; lastMove: MoveShort | null }[] = [
    { fen: startFen, lastMove: null },
  ];

  let chess: SanChess | null = null;
  try {
    chess = new SanChess(startFen);
  } catch {
    chess = null;
  }

  if (!chess) return positions;

  for (const move of moves) {
    try {
      chess.move(move);
      positions.push({ fen: chess.fen(), lastMove: move });
    } catch {
      break;
    }
  }

  return positions;
}

export default function PuzzleBoard(props: PuzzleBoardProps) {
  const settings = useSettings();
  const boardSize = settings.boardSize || 504;
  const boardSettings = useMemo(
    () => ({
      ...settings,
      chessSet: ChessSets.STAUNTY,
    }),
    [settings],
  );
  const user = useUser();
  const [localElo, setLocalElo] = useState(
    props.userElo || STARTING_PUZZLE_RATING,
  );
  const [eloChange, setEloChange] = useState<number | null>(null);
  const resultRecordedRef = useRef(false);
  const ratingRevealed = props.ratingRevealed ?? true;

  useEffect(() => {
    setLocalElo(props.userElo || STARTING_PUZZLE_RATING);
  }, [props.userElo]);

  const applyRatingChange = (ratingUpdate: PuzzleRatingUpdate) => {
    setLocalElo(ratingUpdate.newElo);
    setEloChange(ratingUpdate.eloChange);
    props.onRatingChange?.(ratingUpdate);
  };

  const updateElo = async (won: boolean, cleanSolve: boolean) => {
    if (user && user.username && props.puzzleRating) {
      try {
        const response = await api.post("/newpuzzle/rating", {
          puzzleRating: props.puzzleRating,
          won,
          cleanSolve,
        });
        const ratingUpdate = response.data as PuzzleRatingUpdate;
        ratingUpdate.won = won;
        applyRatingChange(ratingUpdate);
      } catch (e) {
        console.error("Error updating rating", e);
      }
    } else if (props.puzzleRating) {
      const currentElo = props.userElo || localElo || STARTING_PUZZLE_RATING;
      const expectedScore =
        1 / (1 + Math.pow(10, (props.puzzleRating - currentElo) / 400));
      const actualScore = won ? 1 : 0;
      const change = Math.round(
        (props.ratingKFactor || 32) * (actualScore - expectedScore),
      );
      const nextCleanSolveStreak =
        won && cleanSolve ? (props.cleanSolveStreak || 0) + 1 : 0;
      const streakBonus =
        won && cleanSolve && ratingRevealed
          ? getPuzzleStreakBonus(nextCleanSolveStreak)
          : 0;
      const basePoints = won && ratingRevealed ? Math.max(1, change) : 0;
      const pointsEarned = basePoints + streakBonus;
      applyRatingChange({
        newElo: currentElo + change,
        eloChange: change,
        won,
        points: props.puzzlePoints + pointsEarned,
        pointsEarned,
        streakBonus,
        cleanSolveStreak: nextCleanSolveStreak,
      });
    }
  };

  const [moveIndex, setMoveIndex] = useState(0);
  const [moves, setMoves] = useState<MoveShort[]>([]);
  const [playingWhite, setPlayingWhite] = useState(false);
  const [possibleMoves, setPossibleMoves] = useState({});
  const [fen, setFen] = useState<string | null>(null);
  const [animate, setAnimate] = useState(false);
  const [lastMove, setLastMove] = useState(null);
  const [lives, setLives] = useState(3);
  const [mistakeCount, setMistakeCount] = useState(0);
  const [usedHelp, setUsedHelp] = useState(false);
  const [puzzleState, setPuzzleState] = useState(PuzzleState.START);
  const [highLightKey, setHighLightKey] = useState(null);
  const [solutionVisible, setSolutionVisible] = useState(false);
  const [viewMoveIndex, setViewMoveIndex] = useState<number | null>(null);
  const [difficultyOpen, setDifficultyOpen] = useState(false);
  const chess = useRef(Chess()).current;

  const puzzlePositions = useMemo(
    () => buildPuzzlePositions(props.fen, moves),
    [props.fen, moves],
  );
  const selectedMoveIndex = viewMoveIndex ?? moveIndex;
  const viewedPosition =
    viewMoveIndex !== null ? puzzlePositions[viewMoveIndex] : null;
  const boardFen = viewedPosition?.fen ?? fen;
  const boardLastMove = viewedPosition ? viewedPosition.lastMove : lastMove;
  const boardPossibleMoves = viewMoveIndex === null ? possibleMoves : {};
  const canStepBack = selectedMoveIndex > 0;
  const canStepForward = selectedMoveIndex < moveIndex;

  const recordResult = (won: boolean, cleanSolve = false) => {
    if (resultRecordedRef.current) return;
    resultRecordedRef.current = true;
    updateElo(won, cleanSolve);
    props.onResult?.(won, cleanSolve);
  };

  let title = <></>;
  let text = <></>;
  const ratingChangeText =
    eloChange === null ? null : ratingRevealed ? (
      <span
        className={cn(
          "ml-1 font-bold",
          eloChange >= 0 ? "text-green-600" : "text-red-600",
        )}
      >
        Rating {localElo} ({eloChange >= 0 ? "+" : ""}
        {eloChange})
      </span>
    ) : (
      <span className="ml-1 font-bold text-gray-600">Level adjusted</span>
    );

  switch (puzzleState) {
    case PuzzleState.START:
      title = (
        <span className="flex items-center gap-2 text-lg font-semibold text-gray-800">
          Your move
        </span>
      );
      text = (
        <span className="text-muted-foreground">
          Find the best move for {playingWhite ? "white" : "black"}.
        </span>
      );
      break;

    case PuzzleState.CORRECT_MOVE:
      title = (
        <span className="flex items-center gap-2 text-lg font-semibold text-green-600">
          <MoveIcon moveType={MoveType.BEST} /> <span>Best move</span>
        </span>
      );
      text = <span className="text-muted-foreground">Keep going.</span>;
      break;

    case PuzzleState.INCORRECT_MOVE:
      title = (
        <span className="flex items-center gap-2 text-lg font-semibold text-red-500">
          <MoveIcon moveType={MoveType.MISTAKE} /> <span>Try again</span>
        </span>
      );
      text = (
        <span className="text-muted-foreground">
          That move misses the tactic.
        </span>
      );
      break;

    case PuzzleState.DONE:
      title = (
        <span className="flex items-center gap-2 text-lg font-semibold text-altblue-500">
          <MoveIcon moveType={MoveType.EXCELLENT} /> <span>Completed</span>
        </span>
      );
      text = (
        <span className="text-muted-foreground">
          Puzzle solved
          {ratingChangeText}
        </span>
      );
      break;

    case PuzzleState.FAILED:
      title = (
        <span className="flex items-center gap-2 text-lg font-semibold text-red-600">
          <MoveIcon moveType={MoveType.MISTAKE} /> <span>No lives left</span>
        </span>
      );
      text = (
        <span className="text-muted-foreground">
          Start a new puzzle
          {ratingChangeText}
        </span>
      );
      break;

    default:
      break;
  }

  function startPuzzle() {
    if (!props.fen) return;

    const valid = chess.load(props.fen, 1);
    if (!valid) return;

    setPuzzleState(PuzzleState.START);
    setLives(3);
    setMistakeCount(0);
    setMoveIndex(1);
    setEloChange(null);
    setSolutionVisible(false);
    setUsedHelp(false);
    setViewMoveIndex(null);
    setHighLightKey(null);
    resultRecordedRef.current = false;

    const puzzleMoves =
      typeof props.moves === "string"
        ? (props.moves.split(" ").filter(Boolean) as UciMove[]).map((move) =>
            uci2move(move),
          )
        : props.moves;

    const firstMove = puzzleMoves[0];
    if (!firstMove) return;

    chess.move(firstMove);
    setLastMove(firstMove);
    const fen = chess.fen();
    const playingWhite = chess.turn() === "w";

    setPlayingWhite(playingWhite);
    setMoves(puzzleMoves);
    setPossibleMoves(convertMoves(chess.moves({ verbose: true })));
    setAnimate(true);
    setFen(fen);
  }

  useEffect(() => {
    startPuzzle();
  }, [props.fen]);

  function moveMade(move) {
    if (
      viewMoveIndex !== null ||
      !moves.length ||
      puzzleState === PuzzleState.DONE ||
      puzzleState === PuzzleState.FAILED
    ) {
      return;
    }

    setHighLightKey(null);
    const correctMove = moves[moveIndex];
    if (!correctMove) return;

    if (
      move.from === correctMove.from &&
      move.to === correctMove.to &&
      move.promotion === correctMove.promotion
    ) {
      if (moves.length === moveIndex + 1) {
        setFen(move.fen);
        setPossibleMoves({});
        setPuzzleState(PuzzleState.DONE);
        setMoveIndex(moveIndex + 1);
        recordResult(true, mistakeCount === 0 && !usedHelp);
        props.onComplete?.();
      } else {
        const nextMove = moves[moveIndex + 1];
        chess.move(move);
        setMoveIndex(moveIndex + 1);

        chess.move(nextMove);
        const fen = chess.fen();
        const nextPossibleMoves = convertMoves(chess.moves({ verbose: true }));
        setTimeout(() => {
          setFen(fen);
          setPossibleMoves(nextPossibleMoves);
          setMoveIndex(moveIndex + 2);
          setLastMove(nextMove);
        }, 300);
        setPuzzleState(PuzzleState.CORRECT_MOVE);
      }
    } else {
      const newLives = lives - 1;
      setMistakeCount((currentMistakes) => currentMistakes + 1);
      setLives(newLives);
      setFen(move.fen);
      setPossibleMoves(possibleMoves);
      setMoveIndex(moveIndex);

      if (newLives === 0) {
        setPuzzleState(PuzzleState.FAILED);
        recordResult(false, false);
      } else {
        setPuzzleState(PuzzleState.INCORRECT_MOVE);
        setTimeout(() => {
          setFen(fen);
        }, 300);
      }
    }
  }

  const solutionLine = useMemo(
    () => moves.map((move) => move2uci(move)).join(" "),
    [moves],
  );

  const showHint = () => {
    setUsedHelp(true);
    setViewMoveIndex(null);
    const move = moves[moveIndex];
    if (move) setHighLightKey(move.from);
  };

  const stepPuzzleMove = (delta: number) => {
    const nextMoveIndex = Math.max(
      0,
      Math.min(moveIndex, selectedMoveIndex + delta),
    );
    setViewMoveIndex(nextMoveIndex === moveIndex ? null : nextMoveIndex);
    setHighLightKey(null);
  };

  const puzzleFinished =
    puzzleState === PuzzleState.DONE || puzzleState === PuzzleState.FAILED;
  const panelHeight = settings.isDesktop ? boardSize + 44 + "px" : undefined;
  const hasPlayedSolutionMove = moveIndex > 1;
  const progressBar = (
    <PuzzleProgressBar
      rating={localElo}
      leagueName={props.leagueName}
      ratingRevealed={ratingRevealed}
      solvedProgress={props.solvedProgress}
      puzzlePoints={props.puzzlePoints}
      lastPointsEarned={props.lastPointsEarned}
      lastStreakBonus={props.lastStreakBonus}
      cleanSolveStreak={props.cleanSolveStreak}
      nextRank={props.nextRank}
      nextRankPoints={props.nextRankPoints}
    />
  );

  return (
    <div className="flex flex-col items-center md:mt-4">
      <div className="flex flex-col items-center w-full md:w-auto md:flex-row md:items-start md:rounded-lg">
        <div className="flex justify-center md:mr-4">
          <ResizableBoardFrame
            size={boardSize}
            enabled={settings.isDesktop !== false}
          >
            <Board
              lastMove={boardLastMove}
              settings={boardSettings}
              fen={boardFen}
              animate={animate}
              size={boardSize}
              playingWhite={playingWhite}
              possibleMoves={boardPossibleMoves}
              onMoveMade={(move) => moveMade(move)}
              highLightKey={highLightKey}
              showLastMove
            />
          </ResizableBoardFrame>
        </div>

        <div
          className="mb-20 flex h-full w-full flex-col items-stretch rounded-md bg-athens-gray-150 text-sm md:mb-0 md:w-[400px] lg:w-[400px]"
          style={{ height: panelHeight }}
        >
          <div className="hidden items-center justify-between gap-3 rounded-t-md bg-altblue-500 px-5 py-3 font-bold text-athens-gray-50 md:flex text-small">
            <div className="flex min-w-0 items-center gap-2">
              <FaPuzzlePiece className="text-sm shrink-0" />
              <span className="truncate">Puzzle Challenge</span>
            </div>
            <PuzzleUserStanding
              rating={localElo}
              leagueName={props.leagueName}
              ratingRevealed={ratingRevealed}
            />
          </div>

          <div className="flex flex-col h-full min-h-0 p-3 overflow-hidden md:p-4">
            <div className="mb-3 md:hidden">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-lg font-bold text-gray-800">
                    Puzzle Challenge
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Build your puzzle rating
                  </div>
                </div>
                <div className="shrink-0 rounded-full bg-altblue-500 px-3 py-1 text-xs font-bold text-white">
                  {ratingRevealed ? localElo : "Placement"}
                </div>
              </div>
            </div>

            <PuzzleStatusCard
              statusTitle={title}
              statusText={text}
              puzzleRating={props.puzzleRating}
              lives={lives}
            />

            {!ratingRevealed && progressBar}

            {hasPlayedSolutionMove && (
              <PuzzleMoveList
                startFen={props.fen}
                moves={moves}
                moveIndex={moveIndex}
                selectedMoveIndex={selectedMoveIndex}
              />
            )}

            {solutionVisible && (
              <PuzzleSolutionLine solutionLine={solutionLine} />
            )}

            <div className="flex-1 min-h-0" />

            {!user?.username && (
              <Link
                href="/register"
                className="px-3 py-2 mt-3 text-xs font-medium rounded-md bg-altblue-50 text-altblue-700 hover:bg-altblue-100"
              >
                Create an account to save your puzzle rating and join the puzzle
                league.
              </Link>
            )}

            {ratingRevealed && progressBar}

            <div className="pt-3 mt-3">
              {puzzleFinished ? (
                <Button
                  variant="default"
                  size="lg"
                  className="w-full py-6 text-base font-semibold shadow-md"
                  disabled={props.nextLoading}
                  onClick={() => props.onNext?.()}
                >
                  Next puzzle
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="lg"
                  className="w-full"
                  onClick={showHint}
                >
                  Hint
                </Button>
              )}
              <div className="flex items-center justify-between gap-2 mt-2">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-0 text-gray-600 h-9 w-11 hover:bg-athens-gray-200"
                    disabled={!canStepBack}
                    onClick={() => stepPuzzleMove(-1)}
                    aria-label="Previous puzzle move"
                  >
                    <FaStepBackward />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-0 text-gray-600 h-9 w-11 hover:bg-athens-gray-200"
                    disabled={!canStepForward}
                    onClick={() => stepPuzzleMove(1)}
                    aria-label="Next puzzle move"
                  >
                    <FaStepForward />
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-0 text-gray-600 h-9 w-11 hover:bg-athens-gray-200"
                  onClick={() => setDifficultyOpen(true)}
                  aria-label="Puzzle settings"
                >
                  <FaCog />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {difficultyOpen && (
        <PuzzleDifficultyPopup
          selectedDifficulty={props.puzzleDifficulty}
          onSelect={(difficulty) =>
            props.onPuzzleDifficultyChange?.(difficulty)
          }
          onClose={() => setDifficultyOpen(false)}
        />
      )}
    </div>
  );
}
